<template>
  <div class="partners">
    <div class="tile mt-4 pb-4 mb-8">
      <loading-view :loading="false" class="mx-6">
        <div class="grid grid-2">
          <div class="form_justify_left">
            <h1 class="h4">{{ trans('dashboard.partner.share_personal_link') }}</h1>
            <p class="mb-3">
              {{ referralLink }}
              <svg v-if="copySuccess" class="icon-1 mx-1 okIcon">
                <use xlink:href="#ok-icon"></use>
              </svg>
              <svg v-else class="icon-1 mx-1 copyIcon" @click="copyReferralLink()">
                <use xlink:href="#copy-icon"></use>
              </svg>
            </p>

            <h1 class="h4">{{ trans('dashboard.partner.customers.create.title') }}</h1>
            <form role="form" class="pt-2" @submit.prevent="submit">
              <form-input
                v-model="form.first_name"
                type="text"
                id="user-first-name"
                name="first_name"
                :label-text="trans('common.first_name')"
                :placeholder="trans('common.first_name')"
                container-class="form_group"
                :error="form.errors.first_name"
                autofocus
              />

              <form-input
                v-model="form.last_name"
                type="text"
                id="user-last-name"
                :label-text="trans('common.last_name')"
                :placeholder="trans('common.last_name')"
                container-class="form_group"
                :error="form.errors.last_name"
              />

              <form-input
                v-model="form.email"
                type="email"
                id="user-email"
                name="email"
                :label-text="trans('common.email')"
                :placeholder="trans('common.email')"
                container-class="form_group"
                :error="form.errors.email"
              />

              <div class="form_group">
                <form-input
                  id="register-profile_language"
                  type="select"
                  name="profile_language"
                  v-model="form.profile_language"
                  label="name"
                  track-by="code"
                  :options="locales"
                  :allow-empty="false"
                  :error="form.errors.profile_language"
                  :label-text="trans('common.profile_language')"
                  :placeholder="trans('common.profile_language')"
                />
              </div>

              <div class="form_group mt-1">
                <form-input
                  id="register-applications"
                  type="multi-select"
                  v-model="form.applications"
                  track-by="id"
                  label="name"
                  :options="applications"
                  :error="form.errors.applications"
                  :label-text="trans('__JSON__.Which applications are you going to integrate for this customer?')"
                  :placeholder="trans('common.applications')"
                />
              </div>

              <div class="form_grid">
                <div class="form_group">
                  <form-input
                    id="user-billing_address"
                    name="billing_address"
                    type="text"
                    v-model="form.billing_address"
                    :label-text="trans('common.address')"
                    :placeholder="trans('common.address')"
                    :error="form.errors.billing_address"
                  />
                </div>
                <div class="form_group">
                  <form-input
                    id="user-billing_address_line_2"
                    name="billing_address_line_2"
                    type="text"
                    v-model="form.billing_address_line_2"
                    :label-text="trans('common.address_line_2')"
                    :placeholder="trans('common.address_line_2')"
                    :error="form.errors.billing_address_line_2"
                  />
                </div>
              </div>

              <div class="form_group pb-2">
                <form-input
                  id="user-billing_city"
                  name="billing_city"
                  type="text"
                  v-model="form.billing_city"
                  :label-text="trans('common.city')"
                  :placeholder="trans('common.city')"
                  :error="form.errors.billing_city"
                />
              </div>
              <div class="form_grid">
                <div class="form_group">
                  <form-input
                    id="user-billing_state"
                    name="billing_state"
                    type="text"
                    v-model="form.billing_state"
                    :label-text="trans('common.state')"
                    :placeholder="trans('common.state')"
                    :error="form.errors.billing_state"
                  />
                </div>
                <div class="form_group">
                  <form-input
                    id="user-billing_zip"
                    name="billing_zip"
                    type="text"
                    v-model="form.billing_zip"
                    :label-text="trans('common.postal_code')"
                    :placeholder="trans('common.postal_code')"
                    :error="form.errors.billing_zip"
                  />
                </div>
              </div>
              <div class="form_group">
                <form-input
                  id="user-billing_country"
                  name="billing_country"
                  type="select"
                  v-model="form.billing_country"
                  :options="countries"
                  :allow-empty="false"
                  track-by="code"
                  label="name"
                  :label-text="trans('common.country')"
                  :error="form.errors.billing_country"
                />
              </div>

              <div class="form_row pt-2">
                <span></span>
                <div>
                  <inertia-link
                    :href="$route('dashboard.partner.customers.show')"
                    class="button button_cancel"
                    :disabled="form.processing"
                  >
                    <span>{{ trans('common.cancel') }}</span>
                  </inertia-link>

                  <form-button :value="trans('common.save')" :processing="form.processing"/>
                </div>
              </div>
            </form>
          </div>
          <div class="h-full flex">
            <div class="flex flex-wrap align-content-center">
              <h3 class="h4 mx-auto">
                {{ trans('dashboard.partner.customers.create.explainer_title') }}
              </h3>
              <p class="mx-auto" style="line-height: 3rem">
                <strong>{{ trans('dashboard.partner.customers.create.explainer_strong_text') }}</strong>
                {{ trans('dashboard.partner.customers.create.explainer_text') }}
              </p>
            </div>
          </div>
        </div>
      </loading-view>
    </div>
  </div>
</template>

<script setup>
import {useForm} from '@inertiajs/vue3';
import Swal from 'sweetalert2';
import FormInput from '@/components/Shared/Form/Input/Index';
import FormButton from '@/components/Shared/Form/Button';
import {computed, inject, onBeforeMount, ref} from 'vue';
import axios from 'axios';

const trans = inject('trans');

const props = defineProps({
  locales: Array,
  currentLocale: String,
  user: Object,
});

const profileLanguage = computed(() => props.locales.find(locale => locale.code === props.currentLocale));
const countries = ref([]);
const applications = ref([]);
const referralLink = ref(props.user.partner?.customerReferralLink);
const copySuccess = ref(false);
const form = useForm({

  first_name: '',
  last_name: '',
  email: '',
  password: '',
  password_confirmation: '',
  billing_address: '',
  billing_address_line_2: '',
  billing_zip: '',
  billing_city: '',
  billing_state: '',
  billing_country: '',
  profile_language: {...profileLanguage.value},
  applications: [],
});


const submit = () => {
  form
    .transform(data => ({
      ...data,
      billing_country: data.billing_country?.code,
      profile_language: data.profile_language?.code,
    }))
    .post(route('partner.customers.store'), {
      onSuccess: () => {
        Swal.fire({
          title: trans('commom.Success'),
          text: trans('dashboard.partner.customers.create.success_message'),
          icon: 'success',
          showConfirmButton: false,
          timer: 3000,
        });
      },
    });
};

const copyReferralLink = () => {
  navigator.clipboard.writeText(referralLink.value);
  copySuccess.value = true
  let interval = setInterval(() => {
    copySuccess.value = false
    clearInterval(interval);
  }, 1500)
};

onBeforeMount(async () => {
  // Fetch countries...
  await axios.get(route('countries')).then(({data}) => {
    countries.value = data.data.countries;
  });

  // Fetch applications...
  await axios.get('/register/all-applications').then(({data}) => {
    applications.value = data.data.applications;
  });
});
</script>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue';

export default {
  layout: DashboardLayout,
};
</script>

<style>
.copyIcon:hover {
  cursor: pointer;
}

.okIcon {
  fill: forestgreen;
}
</style>
